<template>
  <div>
    <div class="row align-items-end mb-3" v-if="canManagePricing">
      <div class="col-md-4">
        <label for="pricing-branch">Pricing for</label>
        <select id="pricing-branch" class="form-control" v-model="selectedBranchId"
          :disabled="isLoad || optionsLoading || pendingSaves > 0 || isDownloading" @change="changeBranch">
          <option :value="null">Default pricing</option>
          <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.code }} - {{ branch.name }}</option>
        </select>
      </div>
      <div class="col-md-4 pt-2 text-primary" aria-live="polite">
        {{ isBranchPricing ? 'Editing prices for ' + selectedBranchName : 'Editing product defaults' }}
      </div>
      <div v-if="isBranchPricing" class="col-md-4 pt-2 text-md-right">
        <button type="button" class="btn btn-primary" :disabled="isLoad || optionsLoading || isDownloading"
          @click="downloadPriceList" title="Download the full price list for this branch">
          {{ isDownloading ? 'Generating PDF...' : 'Download price list (PDF)' }}
        </button>
      </div>
    </div>
    <div class="alert alert-info">
      Use the TAB key to move between fields. Prices are saved when you leave a changed field.
      <template v-if="isBranchPricing"><br>Leave a branch price blank to use the current product default.</template>
    </div>
    <div v-if="optionsError" class="alert alert-danger" role="alert">
      {{ optionsError }} <button type="button" class="btn btn-sm btn-light" @click="loadOptions">Retry</button>
    </div>
    <div v-if="loadError" class="alert alert-danger" role="alert">
      {{ loadError }} <button type="button" class="btn btn-sm btn-light" @click="getProducts(currentQuery)">Retry</button>
    </div>
    <div v-if="downloadError" class="alert alert-danger" role="alert">{{ downloadError }}</div>
    <data-table
      ref="pricingTable"
      :items="items"
      v-on:updateData="getProducts"
      :sortOptions="sortOptions"
      :filterOptions="filterOptions"
      :sort="sort"
      :isLoad="isLoad"
      :page="page"
      :rowsPerPage="rowsPerPage"
      :fromRow="fromRow"
      :toRow="toRow"
      :totalRows="totalRows"
      :lastPage="lastPage"
    >
      <template v-slot:headings>
        <template v-if="isBranchPricing">
          <div class="col-md-4">CODE / PRODUCT</div>
          <div v-for="field in priceFields" :key="field.key" class="col-md-4 text-right">
            <div class="form-row">
              <div class="col-8">{{ field.label }}</div>
            </div>
          </div>
        </template>
        <template v-else>
        <div class="col-md-2">CODE</div>
        <div class="col-md-1">HOLD</div>
        <div class="col-md-1">PROMOTE</div>
        <div class="col-md-2 text-right">CASES PER PALLET</div>
        <div class="col-md-2 text-right">MIN (Mandate)</div>
        <div class="col-md-2 text-right">MAX (List Price)</div>
        <div class="col-md-2 text-right">AVG MASS</div>
        </template>
      </template>

      <template v-slot:rows="slotProps">
        <template v-if="isBranchPricing">
          <div class="col-12 col-md-4 mb-2 mb-md-0">
            {{ slotProps.item.code }} - {{ slotProps.item.name }}
          </div>
          <div v-for="field in priceFields" :key="field.key" class="col-6 col-md-4 text-right">
            <div class="form-row align-items-center">
              <div class="col-8">
                <label class="d-md-none small" :for="'branch-price-' + slotProps.item.id + '-' + field.key">{{ field.label }}</label>
                <input :id="'branch-price-' + slotProps.item.id + '-' + field.key" type="number" step="0.01" min="0" max="9999999.99"
                  class="form-control branch-price-input ml-auto" :class="{ 'is-invalid': slotProps.item._errors[field.key] }"
                  :aria-label="slotProps.item.code + ' ' + field.label" :disabled="!canManagePricing || isDownloading"
                  :aria-describedby="hasPrice(slotProps.item._branch[field.key]) ? 'branch-default-' + slotProps.item.id + '-' + field.key : null"
                  :aria-invalid="!!slotProps.item._errors[field.key]"
                  :placeholder="priceText(slotProps.item[field.key])" v-model="slotProps.item._branch[field.key]"
                  @change="updateBranchPrice(slotProps.item, field.key, $event)">
              </div>
              <div class="col-4 small text-muted text-left">
                <span v-if="hasPrice(slotProps.item._branch[field.key])" :id="'branch-default-' + slotProps.item.id + '-' + field.key">
                  Default<br>{{ hasPrice(slotProps.item[field.key]) ? 'R' + priceText(slotProps.item[field.key]) : 'Not set' }}
                </span>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
        <div class="col-md-2">
          {{ slotProps.item.code }} -  {{ slotProps.item.name }}
        </div>
      
        <div class="col-md-1">
          <b-form-checkbox
            v-model="slotProps.item.is_active"
            value="2"
            unchecked-value="1"
            @change="updateProduct(slotProps.item, 'is_active')"
            :disabled="!canManageProducts"
            switch
          >
        </b-form-checkbox>
        </div>
         <div class="col-md-1">
          <b-form-checkbox
            v-model="slotProps.item.on_promotion"
            value="1"
            unchecked-value="0"
            @change="updateProduct(slotProps.item, 'on_promotion')"
            :disabled="!canManageProducts"
            switch
          >
          </b-form-checkbox>
        </div>
        <div class="col-md-2 text-right">
          <input
            @change="updateProduct(slotProps.item, 'cases_per_pallet')"
            :disabled="!canManageProducts"
            style="height: 30px; max-width: 110px"
            v-model="slotProps.item.cases_per_pallet"
            type="number"
            step="1"
            class="form-control float-right"
          />
        </div>
        <div class="col-md-2 text-right">
          <input
            @change="updateProduct(slotProps.item, 'mandate_pricing_min')"
            :disabled="!canManageProducts || !canManagePricing"
            style="height: 30px; max-width: 110px"
            v-model="slotProps.item.mandate_pricing_min"
            type="number"
            step="0.01"
            class="form-control float-right"
          />
        </div>
        <div class="col-md-2 text-right">
          <input
            @change="updateProduct(slotProps.item, 'mandate_pricing_max')"
            :disabled="!canManageProducts || !canManagePricing"
            style="height: 30px; max-width: 110px"
            v-model="slotProps.item.mandate_pricing_max"
            type="number"
            step="0.01"
            class="form-control float-right"
          />
        </div>
        <div class="col-md-2 text-right">
          <input
            @change="updateProduct(slotProps.item, 'average_mass')"
            :disabled="!canManageProducts"
            style="height: 30px; max-width: 110px"
            v-model="slotProps.item.average_mass"
            type="number"
            step="0.01"
            class="form-control float-right"
          />
        </div>
        </template>
        <div class="col-12 small mt-1" aria-live="polite" v-if="slotProps.item._pending || slotProps.item._hasSaved">
          {{ slotProps.item._pending ? 'Saving...' : Object.keys(slotProps.item._errors).length ? 'Not saved' : 'Saved' }}
        </div>
        <div class="col-12 small text-danger mt-1" role="alert" v-if="Object.keys(slotProps.item._errors).length">
          {{ Object.values(slotProps.item._errors).join(' ') }}
        </div>
      </template>
    </data-table>
  </div>
</template>

<script>
import productRepo from "@/api/repos/productRepo";
import dataTable from "@/components/common/DataTable";

export default {
  components: {
    dataTable: dataTable,
  },

  data() {
    return {
      selectedBranchId: null,
      branches: [],
      optionsLoading: false,
      optionsError: '',
      loadError: '',
      loadRequestId: 0,
      pendingSaves: 0,
      isDownloading: false,
      downloadError: '',
      currentQuery: null,
      priceFields: [
        { key: 'mandate_pricing_min', label: 'MIN (Mandate)' },
        { key: 'mandate_pricing_max', label: 'MAX (List Price)' },
      ],
      isLoad: false,
      sort: { column: "code", label: "Code Asc", order: "asc" },
      sortOptions: [
        { column: "name", label: "Name Asc", order: "asc" },
        { column: "name", label: "Name Desc", order: "desc" },
        { column: "created_at", label: "Date Created Asc", order: "asc" },
        { column: "created_at", label: "Date Created Desc", order: "desc" },
        { column: "code", label: "Code Asc", order: "asc" },
        { column: "code", label: "Code Desc", order: "desc" },
        {
          column: "mandate_pricing_min",
          label: "Mandate Price Asc",
          order: "asc",
        },
        {
          column: "mandate_pricing_min",
          label: "Mandate Price Desc",
          order: "desc",
        },
      ],
      filterOptions: [],
      page: 1,
      rowsPerPage: localStorage.getItem("rowsPerPage")
        ? localStorage.getItem("rowsPerPage")
        : 12,
      fromRow: 0,
      toRow: 0,
      totalRows: 0,
      lastPage: 0,
      items: [],
    };
  },
  mounted() {
    this.loadOptions();
    this.getProducts({
      sort: this.sort,
      filters: null,
      page: this.page,
      rowsPerPage: localStorage.getItem("rowsPerPage")
        ? localStorage.getItem("rowsPerPage")
        : 12,
      search: "",
    });
  },
  computed: {
    isBranchPricing() { return this.selectedBranchId !== null; },
    canManagePricing() { return this.userHasPerms(['Manage pricing']); },
    canManageProducts() { return this.userHasPerms(['Manage products']); },
    selectedBranchName() {
      const branch = this.branches.find(branch => branch.id === this.selectedBranchId);
      return branch ? branch.name : '';
    },
  },
  async beforeRouteLeave(to, from, next) {
    await this.waitForSaves();
    next();
  },
  methods: {
    async loadOptions() {
      this.optionsLoading = true;
      this.optionsError = '';
      try {
        const [filters, branches] = await Promise.all([
          productRepo.createGetData(),
          this.canManagePricing ? productRepo.pricingBranches() : Promise.resolve({ data: { branches: [] } }),
        ]);
        this.branches = branches.data.branches;
        this.setFilterData(filters.data);
      } catch (error) {
        this.optionsError = 'Could not load pricing options. ' + this.errorMessage(error);
      } finally {
        this.optionsLoading = false;
      }
    },
    setFilterData(data) {
      this.filterOptions = [
        { column: 'is_active', value: '1', label: 'Active' },
        { column: 'is_active', value: '2', label: 'On Hold' },
      ];

      this.filterOptions.push({
        column: "group-heading",
        label: "groups",
      });

      data.product_groups.forEach((p) => {
        this.filterOptions.push({
          column: "product_group_id",
          value: p.id,
          label: p.name,
        });
      });

      this.filterOptions.push({
        column: "group-heading",
        label: "suppliers",
      });

      data.suppliers.forEach((s) => {
        this.filterOptions.push({
          column: "supplier_id",
          value: s.id,
          label: s.name,
        });
      });

      this.filterOptions.push({
        column: "group-heading",
        label: "brands",
      });

      data.brands.forEach((b) => {
        this.filterOptions.push({
          column: "brand_id",
          value: b.id,
          label: b.name,
        });
      });
    },

    async getProducts(e) {
      const requestId = ++this.loadRequestId;
      this.currentQuery = { ...e };
      this.isLoad = true;
      this.loadError = '';
      const branchId = this.selectedBranchId;
      try {
        await this.waitForSaves();
        if (requestId !== this.loadRequestId) return;
        const args = [
          e.page,
          e.rowsPerPage,
          e.sort.column,
          e.sort.order,
          e.search,
          e.filters != null ? e.filters : null
        ];
        const { data } = branchId === null
          ? await productRepo.productsPricing(...args)
          : await productRepo.branchPricing(branchId, ...args);
        if (requestId !== this.loadRequestId) return;

        this.items = data.products.data.map(product => this.prepareProduct(product));
        this.totalRows = data.products.total;
        this.page = data.products.current_page;
        this.fromRow = data.products.from;
        this.toRow = data.products.to;
        this.lastPage = data.products.last_page;

      } catch (error) {
        if (requestId === this.loadRequestId) {
          this.items = [];
          this.loadError = 'Could not load prices. ' + this.errorMessage(error);
        }
      } finally {
        if (requestId === this.loadRequestId) this.isLoad = false;
      }
    },

    prepareProduct(product) {
      const branch = (product.product_branch_prices || [])[0] || {};
      const values = {
        mandate_pricing_min: branch.mandate_pricing_min == null ? null : branch.mandate_pricing_min,
        mandate_pricing_max: branch.mandate_pricing_max == null ? null : branch.mandate_pricing_max,
      };
      return { ...product, _branch: { ...values }, _savedBranch: { ...values }, _savedDefaults: { ...product },
        _pending: 0, _errors: {}, _hasSaved: false, _saveQueue: null };
    },
    changeBranch() {
      this.downloadError = '';
      if (this.$refs.pricingTable) this.$refs.pricingTable.currentPage = 1;
      return this.getProducts({ ...this.currentQuery, page: 1 });
    },
    hasPrice(value) { return value !== null && value !== undefined && value !== ''; },
    priceText(value) {
      return this.hasPrice(value) ? Number(value).toFixed(2) : '';
    },
    errorMessage(error) {
      const data = error.response && error.response.data;
      if (data && data.errors) return Object.values(data.errors).flat().join(' ');
      return (data && data.message) || 'Please try again.';
    },
    async waitForSaves() {
      const products = this.items;
      while (products.some(product => product._pending > 0)) {
        await Promise.all(products.map(product => product._saveQueue));
      }
    },
    async downloadPriceList() {
      if (!this.isBranchPricing || this.isDownloading) return;
      const branchId = this.selectedBranchId;
      this.isDownloading = true;
      this.downloadError = '';
      try {
        await this.waitForSaves();
        if (this.items.some(product => Object.keys(product._errors).length)) {
          this.downloadError = 'Some prices could not be saved. Correct them before downloading the price list.';
          return;
        }
        const response = await productRepo.branchPriceListPdf(branchId);
        const fileUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const filename = /filename="?([^";]+)"?/i.exec((response.headers || {})['content-disposition'] || '');
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = filename ? filename[1] : `price-list-branch-${branchId}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(fileUrl), 1000);
      } catch (error) {
        if (error.response && error.response.data instanceof Blob) {
          try {
            const text = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsText(error.response.data);
            });
            error.response.data = JSON.parse(text);
          } catch (_) { /* Use the generic message if the response is not JSON. */ }
        }
        this.downloadError = 'Could not download the price list. ' + this.errorMessage(error);
      } finally {
        this.isDownloading = false;
      }
    },
    queueSave(product, field, save) {
      product._pending++;
      this.pendingSaves++;
      product._saveQueue = (product._saveQueue || Promise.resolve()).then(async () => {
        try {
          await save();
          this.$delete(product._errors, field);
          product._hasSaved = true;
        } catch (error) {
          this.$set(product._errors, field, 'Not saved. ' + this.errorMessage(error));
        } finally {
          product._pending--;
          this.pendingSaves--;
        }
      });
      return product._saveQueue;
    },
    updateBranchPrice(product, field, event) {
      const branchId = this.selectedBranchId;
      const value = product._branch[field] === '' ? null : product._branch[field];
      const badInput = event && event.target.validity && event.target.validity.badInput;
      const validPrice = value === null || (/^\d+(\.\d{1,2})?$/.test(String(value)) && Number(value) <= 9999999.99);
      if (badInput || !validPrice) {
        this.$set(product._errors, field, 'Not saved. Enter a valid price with up to two decimal places.');
        product._branch[field] = product._savedBranch[field];
        return;
      }
      product._branch[field] = value;
      return this.queueSave(product, field, async () => {
        try {
          const { data } = await productRepo.updateBranchPrice(branchId, product.id, { [field]: value });
          const saved = data.branch_price ? data.branch_price[field] : null;
          product._savedBranch[field] = saved;
          if (product._branch[field] === value) product._branch[field] = saved;
        } catch (error) {
          if (product._branch[field] === value) product._branch[field] = product._savedBranch[field];
          throw error;
        }
      });
    },
    updateProduct(product, field) {
      const value = product[field];
      return this.queueSave(product, field, async () => {
        try {
          // Build from confirmed values when this queued save runs; other drafts may have failed.
          const payload = { ...product._savedDefaults, [field]: value };
          await productRepo.update(product.id, payload);
          product._savedDefaults[field] = value;
        } catch (error) {
          if (product[field] === value) product[field] = product._savedDefaults[field];
          throw error;
        }
      });
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler: function (newVal) {
        this.sidebarToggle = newVal.meta && newVal.meta.sidebarToggle;
      },
    },
  },
};
</script>

<style scoped>
.branch-price-input { max-width: 170px; }
</style>
